import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Ticket, RefreshCw, Settings, ArrowRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import PageHeader from '@/components/PageHeader'
import { raffleAPI, serviceConfigAPI, modulesAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

const fmt = (iso) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('nl-BE',
    { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export default function AdminRafflePage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const [rows, setRows] = useState([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [thresholdEur, setThresholdEur] = useState('10')
  const [moduleOn, setModuleOn] = useState(true)

  const load = async () => {
    try {
      const [r, cfg, mods] = await Promise.all([
        raffleAPI.adminListUsers(),
        serviceConfigAPI.getAll().catch(() => ({ data: { configs: [] } })),
        modulesAPI.getAll().catch(() => ({ data: [] })),
      ])
      setRows(r.data?.users || [])
      const configs = cfg.data?.configs || []
      const drempel = configs.find(c => c.key === 'raffle_ticket_threshold_cents')
      const cents = Number(drempel?.value ?? 1000)
      setThresholdEur(String(cents / 100))
      const mod = (mods.data || []).find(m => m.module_key === 'raffle')
      if (mod) setModuleOn(!!mod.is_enabled)
      setErr(null)
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    }
  }

  useEffect(() => { load() }, [])

  const bewaarDrempel = async () => {
    const eur = Number(thresholdEur)
    if (!Number.isFinite(eur) || eur <= 0) {
      window.alert('Voer een positief bedrag in euro in.')
      return
    }
    setBusy(true)
    try {
      await serviceConfigAPI.update('raffle_ticket_threshold_cents', String(Math.round(eur * 100)))
      await load()
    } catch (e) {
      window.alert(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  const toggleModule = async () => {
    setBusy(true)
    try {
      await modulesAPI.toggle('raffle')
      setModuleOn(!moduleOn)
    } catch (e) {
      window.alert(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  const nuReconcilen = async () => {
    setBusy(true)
    try {
      await raffleAPI.adminReconcile()
      await load()
    } catch (e) {
      window.alert(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  if (!isAdmin) return <div className="p-6 text-muted-foreground">Enkel voor admins.</div>

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <PageHeader
        icon={Ticket}
        eyebrow="Beheer"
        title="Raffle-tickets"
        subtitle="Per schijf besteding in kassa krijgt een user 1 ticket voor kleine spellen ter plaatse"
        actions={
          <Button variant="outline" size="sm" disabled={busy} onClick={nuReconcilen}>
            <RefreshCw className={`h-4 w-4 mr-1 ${busy ? 'animate-spin' : ''}`} />
            Nu reconcilen
          </Button>
        }
      />

      {err && (
        <Card><CardContent className="p-4 text-sm text-destructive">{err}</CardContent></Card>
      )}

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <Settings className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold">Instellingen</span>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="threshold" className="text-xs uppercase tracking-widest text-muted-foreground">
                Drempel (€ per ticket)
              </Label>
              <div className="flex gap-2">
                <Input id="threshold" type="number" min="1" step="1"
                       value={thresholdEur}
                       onChange={e => setThresholdEur(e.target.value)}
                       className="w-32" />
                <Button size="sm" disabled={busy} onClick={bewaarDrempel}>Bewaren</Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Elke € {thresholdEur || '10'} besteed in de kassa = 1 raffle-ticket.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Module-status
              </Label>
              <div className="flex items-center gap-2">
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-[4px] text-sm font-semibold ${moduleOn ? 'bg-acid/10 text-acid' : 'bg-muted text-muted-foreground'}`}>
                  <span className={`h-2 w-2 rounded-full ${moduleOn ? 'bg-acid' : 'bg-muted-foreground'}`} />
                  {moduleOn ? 'Actief' : 'Uit'}
                </div>
                <Button size="sm" variant="outline" disabled={busy} onClick={toggleModule}>
                  {moduleOn ? 'Uitschakelen' : 'Inschakelen'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Bij "Uit" verdwijnt de widget bij users en stoppen nieuwe tickets.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="px-6 py-4 border-b border-border">
            <div className="text-sm font-semibold">Spelers met tickets</div>
            <div className="text-xs text-muted-foreground">Klik door voor detail + inruilen</div>
          </div>
          {rows.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              Nog geen tickets uitgegeven deze LAN.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {rows.map(r => (
                <Link key={r.user_id}
                      to={`/spelers/${r.user_id}`}
                      className="flex items-center gap-4 px-6 py-3 hover:bg-white/[.03] transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{r.display_name || r.username}</div>
                    <div className="text-xs text-muted-foreground">
                      Laatst: {fmt(r.last_granted_at)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold" style={{ color: '#E4C265' }}>{r.open}</div>
                    <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground">Open</div>
                  </div>
                  <div className="text-right w-16">
                    <div className="text-sm font-semibold text-muted-foreground">{r.used}</div>
                    <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground">Ingediend</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
